/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IContextualMenuItem, IContextualMenuListProps } from '@fluentui/react/lib/ContextualMenu';
import * as React from 'react';
import { useEffect } from 'react';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';

type MenuListRenderProps = {
    /**
     * The menu search box placeholder text.
     */
    searchPlaceholder: string;

    /**
     * The depth level of the menus
     */
    depth?: number;

    /**
     * The menu props passed to the on render function.
     */
    props: IContextualMenuListProps;

    /**
     * Show only the action items if enabled.
     */
    actionsOnlyMenu?: boolean;

    /**
     * Determines whether the main contextual menu is hidden or not
     */
    isContextualMenuHidden?: boolean;

    /**
     * Search box submit handler
     */
    onSubmit?: (query: string) => void;

    /**
     * The default render passed to the on render function.
     */
    defaultRender?: (props?: IContextualMenuListProps) => React.ReactNode;

    /**
     * Function used to get the contextual menu item to be used in case of no items match the query.
     */
    getQueryNoMatchMenuItem?: (text: string) => IContextualMenuItem;
};

export const handleMenuListRender = (params: MenuListRenderProps) => <MenuListRenderer {...params} />;
const { styleDiv } = getStylistV2('EntityDropdown');

const itemsPerMenu = 8;
const itemHeight = 36;
const EntitiesSection = styleDiv('EntitiesSection', {
    // Scrollbar appears if there are more than 8 items in the menu.
    maxHeight: itemHeight * itemsPerMenu,
    overflowY: 'auto'
});

const { getClassName } = getStylistV2('EntityDropdown');
const searchBoxClassName = getClassName('searchBox', { margin: '8px' });

const MenuListRenderer = React.memo((params: MenuListRenderProps) => {
    const [searchQuery, setSearchQuery] = React.useState('');
    const {
        props: { items },
        defaultRender,
        getQueryNoMatchMenuItem,
        searchPlaceholder,
        onSubmit,
        depth = 0,
        actionsOnlyMenu = false,
        isContextualMenuHidden = false
    } = params;
    const actionItems = items.filter(i => !i.data);
    const entityItems = substringSearchStrategy(
        items.filter(i => i.data),
        searchQuery,
        'text'
    );

    useEffect(() => {
        if (isContextualMenuHidden) {
            setSearchQuery('');
        }
    }, [isContextualMenuHidden]);

    if (!entityItems.length && searchQuery.length && getQueryNoMatchMenuItem) {
        entityItems.push(getQueryNoMatchMenuItem(searchQuery));
    }

    return (
        <>
            {!actionsOnlyMenu && (
                <>
                    <LuisDebouncedSearchBox
                        size={36}
                        className={searchBoxClassName}
                        data-automation-id={`searchBoxDepth${depth}`}
                        iconProps={{ iconName: 'Filter' }}
                        ariaLabel={searchPlaceholder}
                        placeholder={searchPlaceholder}
                        onClear={() => setSearchQuery('')}
                        onChange={(_ev, text: string) => setSearchQuery(text ?? '')}
                        onKeyUp={e => {
                            if (e.key === LuisKeyCodes.Enter) {
                                onSubmit(searchQuery);
                                setSearchQuery('');
                            }
                        }}
                        value={searchQuery}
                        debounceTime={0}
                    />
                    <LuisSeparator />
                    <EntitiesSection data-automation-id="EntitiesSectionDropdown">
                        {defaultRender({ ...params.props, items: entityItems })}
                    </EntitiesSection>
                    <SearchResultsAnnouncer isActive={searchQuery.length > 0} itemsLength={entityItems.length} />
                </>
            )}
            {defaultRender({ ...params.props, items: actionItems })}
        </>
    );
});
